import type { IClient } from "./Client.js";
import { DataProviderHandlerAPI } from "./DataHandler.js";
import { DataObject, type DataObjectOptions } from "./DataObject.js";
import type { FieldDefinition } from "./fields.js";
import { getFields } from "./utils/index.js";

export type GenerateApiDataObjectOptions<T extends Record<string, unknown>> = Omit<
	Partial<DataObjectOptions<T>>,
	"fields"
> & {
	id: string;
	dynamicLoading?: boolean;
	fields: Array<FieldDefinition | string>;
	timeout?: number;
	client?: IClient;
	resource: string;
	uniqueName?: string;
};

export type GenerateApiDataHandlerOptions<T extends Record<string, unknown>> = Pick<
	GenerateApiDataObjectOptions<T>,
	"resource" | "groupBy" | "timeout" | "fields" | "client" | "uniqueName"
>;

export function generateApiDataHandler<T extends Record<string, unknown>>({
	resource,
	timeout,
	fields,
	client,
	groupBy,
	uniqueName,
}: GenerateApiDataHandlerOptions<T>): DataProviderHandlerAPI<T> {
	if (groupBy && !Array.isArray(groupBy)) {
		groupBy = [String(groupBy)];
	}

	return new DataProviderHandlerAPI({
		dataSourceId: resource,
		timeout,
		fields,
		client,
		groupBy: groupBy as string[],
		uniqueName,
	});
}

export function generateApiDataObject<T extends Record<string, unknown>>(options: GenerateApiDataObjectOptions<T>) {
	let { client, dynamicLoading = false, id, timeout, resource, fields, groupBy, uniqueName } = options;
	if (groupBy && !Array.isArray(groupBy)) {
		groupBy = [String(groupBy)];
	}

	let dataHandler = generateApiDataHandler({
		resource,
		timeout,
		fields,
		client,
		groupBy,
		uniqueName,
	});

	let dataObject = new DataObject<T>(
		Object.assign(options, {
			dataSourceId: id,
			dataHandler,
			fields: getFields(fields),
			dynamicLoading: dynamicLoading === true,
		}),
	);

	return dataObject;
}
